/** Field-level context help for the feature-flag dialogs (see FieldHelp). */
import type { FieldHelpEntry } from '@/components/FieldHelp';

export const FLAG_HELP = {
  feature: {
    title: 'Feature',
    body: 'One of the named content features blocks can declare (pickup, meeting point, tickets, …). The flag controls where blocks carrying this feature render.',
    story: 'notification-feature-flags',
  },
  level: {
    title: 'Level',
    body: 'The scope this flag row applies to. A feature renders only if it is enabled at EVERY applicable level — system, OTA, tenant, tour, notification, and the tag condition.',
    story: 'notification-feature-flags',
  },
  scope: {
    title: 'Scope',
    body: 'The specific record this flag targets at the chosen level. Feature, level and scope are immutable after creation — only the enabled state and comment can change. Tag-condition rows are the exception: their tags, match mode and sources stay editable.',
    story: 'notification-feature-flags',
  },
  enabled: {
    title: 'Enabled',
    body: 'Flag rows are sparse: without any row the feature is enabled. Rows exist to override that, so creating a Disabled row is the common case. There is no delete — revert by toggling back on. Tag rows invert this: Enabled = the condition is enforced, Disabled = suspended.',
    story: 'notification-feature-flags',
  },
  tag_condition: {
    title: 'Tag condition',
    body: 'Tags the rendering context must carry for the feature to render. Pinned chips (#) match one tenant’s exact tag; dashed code chips match by category code + tag name against whichever tenant is rendering. If the selected sources carry no matching tag, the blocks are suppressed.',
    story: 'notification-feature-flags',
  },
  tag_sources: {
    title: 'Tag sources',
    body: 'Which entities’ tags count as “present in the context” — e.g. the booking, tour, OTA, or tenant. A source absent from a render contributes nothing; if the union of the selected sources has no match, the condition fails.',
    story: 'notification-feature-flags',
  },
  polarity: {
    title: 'Polarity',
    body: 'Require is the original meaning: the feature renders only while the tags match — an allow-list. Exclude inverts it: the feature renders only while they do NOT, which is what "never send the app promo to Viator" needs. An exclude condition with no tags would suppress the feature for every booking, so it is refused.',
    story: 'notification-feature-flags',
  },
  channel: {
    title: 'Channel',
    body: 'Restricts the condition to one delivery channel. Empty means every channel, which is how every row behaved before this existed. A channel-scoped row deliberately does not apply to the canonical base render — that is the version kept for audit, and it must not move.',
    story: 'notification-feature-flags',
  },
  tag_tenant: {
    title: 'Operator',
    body: 'Leave empty for a condition that applies to every operator. Setting one scopes the condition to that operator and REPLACES their system row — the two do not combine, so a tenant row states the whole condition. Exactly one row ever applies, narrowest first: operator + channel, then operator, then system + channel, then system.',
    story: 'notification-feature-flags',
  },
  comment: {
    title: 'Comment',
    body: 'Why this flag exists — shown in the grid and attributed to you. Your future colleagues will read it during an incident; write a real one.',
    story: 'notification-feature-flags',
  },
} as const satisfies Record<string, FieldHelpEntry>;
